import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuth } from "./auth";
import { AddSource } from "./screens/AddSource";
import { Dashboard } from "./screens/Dashboard";
import { ScraperDashboard } from "./screens/ScraperDashboard";
import { ReviewFields } from "./screens/ReviewFields";
import { RunDetail } from "./screens/RunDetail";
import { Settings } from "./screens/Settings";
import { SignIn } from "./screens/SignIn";
import { SourceDetail } from "./screens/SourceDetail";
import { StatDictionary } from "./screens/StatDictionary";
import { TransferPortal } from "./screens/TransferPortal";
import { Spinner } from "./components/ui";

/**
 * Opening a portal URL while signed out lands on sign-in rather than showing
 * anything behind it, and remembers where you were headed.
 */
function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <Spinner label="Checking your session" />;
  if (!user) return <Navigate to="/sign-in" replace state={{ from: location.pathname }} />;
  return <>{children}</>;
}

function RedirectIfSignedIn({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner label="Checking your session" />;
  if (user) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <Routes>
      <Route
        path="/sign-in"
        element={
          <RedirectIfSignedIn>
            <SignIn />
          </RedirectIfSignedIn>
        }
      />
      {/*
        There is no sign-up route. Per ADR-0016 access is decided at the door by
        the Google domain check, and a registration form would be a way around
        that gate rather than a second route through it. The path redirects
        rather than 404s so an old bookmark lands somewhere useful.
      */}
      <Route path="/sign-up" element={<Navigate to="/sign-in" replace />} />
      {/*
        The Dashboard is the home page: it is the screen written for everyone
        on the team, where Sources is for whoever configures them. Sources
        moved to `/sources`, which is also the prefix of every screen beneath
        it; `/dashboard` stays reachable for anyone who bookmarked it.
      */}
      <Route
        path="/"
        element={
          <RequireAuth>
            <ScraperDashboard />
          </RequireAuth>
        }
      />
      <Route path="/dashboard" element={<Navigate to="/" replace />} />
      <Route
        path="/sources"
        element={
          <RequireAuth>
            <Dashboard />
          </RequireAuth>
        }
      />
      <Route
        path="/sources/new"
        element={
          <RequireAuth>
            <AddSource />
          </RequireAuth>
        }
      />
      <Route
        path="/sources/:profileId"
        element={
          <RequireAuth>
            <SourceDetail />
          </RequireAuth>
        }
      />
      <Route
        path="/sources/:profileId/fields"
        element={
          <RequireAuth>
            <ReviewFields />
          </RequireAuth>
        }
      />
      <Route
        path="/runs/:runId"
        element={
          <RequireAuth>
            <RunDetail />
          </RequireAuth>
        }
      />
      <Route
        path="/transfer-portal"
        element={
          <RequireAuth>
            <TransferPortal />
          </RequireAuth>
        }
      />
      <Route
        path="/stat-dictionary"
        element={
          <RequireAuth>
            <StatDictionary />
          </RequireAuth>
        }
      />
      <Route
        path="/settings"
        element={
          <RequireAuth>
            <Settings />
          </RequireAuth>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
